import AnimationClip from '../AnimationClip';
import GameObject from '../../../GameObject/GameObject';
import Renderable from '../../../Component/Renderable';
import { Easing, Tween } from '@tweenjs/tween.js';
import {
    AwaitIterators,
    ConcatIterators,
    PlayAnimation,
} from '../AnimationHelper';
import { DragonbonesDefault } from './DragonbonesDefault';
import Armature from '../../../Prototype/Armature';
import { delay } from '../../../Utility/Task/TaskQueue';
import { tVector4 } from '../../../Utility/ArrayHelper';
import ElementComponent from '../../../Component/UI/Element';
import Point from '../../../Utility/Point';

interface IDayItemCollectMeta {
    button: GameObject;
    isLastDay: boolean;
    rewardsContainer: GameObject;
    giftArmature: Armature;
    checkArmature: Armature;
}

const lastDayRewardOffsets: { [key: number]: tVector4[] } = {
    2: [
        [0, 50, 0, 50],
        [0, -50, 0, -50],
    ],
    3: [
        [0, 50, 0, 50],
        [50, -50, 50, -50],
        [-50, -50, -500, -50],
    ],
};
const defaultRewardOffsets: { [key: number]: tVector4[] } = {
    1: [[0, 0, 0, 0]],
    2: [
        [40, 0, 40, 0],
        [-40, 0, -40, 0],
    ],
    3: [
        [0, 0, 0, 0],
        [60, 0, 60, 0],
        [-60, 0, -60, 0],
    ],
};

export class DayItemCollect extends AnimationClip<IDayItemCollectMeta> {
    public *execute(
        gameObject: GameObject,
        meta?: IDayItemCollectMeta
    ): IterableIterator<number> {
        const buttonRenderable: Renderable = meta.button.getComponent(
            Renderable
        );
        const giftArmature = meta.giftArmature;
        const gameObjectRenderable: Renderable = giftArmature.getComponent(
            Renderable
        );

        const animsToPlay = [];

        if (meta.isLastDay) {
            const armatureAnim = GameObject.Instantiate(Armature, {
                dragonbonesName: 'daily_login_animations',
                armatureName: 'day_7_collect_effect',
                playOnStart: false,
                alpha: 0,
            });
            gameObject.addChild(armatureAnim);
            const armatureRenderable: Renderable = armatureAnim.getComponent(
                Renderable
            );
            animsToPlay.push(
                PlayAnimation(
                    new DragonbonesDefault(armatureAnim, {
                        playTimes: 1,
                    })
                )
            );
            animsToPlay.push(
                this.startTween(this.getAlphaTween(armatureRenderable, 1, 400))
            );
            animsToPlay.push(
                this.startTween(
                    this.getAlphaTween(gameObjectRenderable, 0, 400)
                )
            );
            gameObject.removeChild(giftArmature);
        } else {
            animsToPlay.push(
                PlayAnimation(
                    new DragonbonesDefault(giftArmature, {
                        playTimes: 1,
                        animationName: 'open',
                    })
                )
            );
        }

        animsToPlay.push(
            this.startTween(this.getAlphaTween(buttonRenderable, 0, 500))
        );

        // TODO: consider creating and adding the rewardIcons here instead of relying on .children to have what we need
        // Can also add in some popin/entry animation effect here for each reward
        const rewardTweens = this.getRewardTweens(
            meta.rewardsContainer.children
        );

        const rewardsRenderable: Renderable = meta.rewardsContainer.getComponent(
            Renderable
        );
        rewardsRenderable.alpha = 0;
        rewardsRenderable.visible = true;
        animsToPlay.push(
            ConcatIterators([
                this.startTween(this.getAlphaTween(rewardsRenderable, 1, 500)),
                AwaitIterators(rewardTweens),
            ])
        );

        yield* AwaitIterators(animsToPlay);
        buttonRenderable.visible = false;
        yield* delay(350);

        yield* this.playCheckAnimation(meta.checkArmature, 0);

        yield* delay(350);
    }
    private getRewardTweens(
        rewardObjects: GameObject[]
    ): IterableIterator<number>[] {
        const animsToPlay: IterableIterator<number>[] = [];

        for (let i = 0; i < rewardObjects.length; i++) {
            const reward = rewardObjects[i];

            const rewardElement = reward.getComponent(ElementComponent);
            const rewardRenderable: Renderable = reward.getComponent(
                Renderable
            );
            rewardRenderable.alpha = 0;

            const newOffset: tVector4 = this.getRewardOffsets(
                rewardObjects,
                i,
                this.meta.isLastDay
            );

            animsToPlay.push(
                this.startTween(this.getAlphaTween(rewardRenderable, 1, 550))
            );
            const rewardScale = rewardElement.scale.clone();
            if (rewardObjects.length > 1) {
                rewardElement.scale = Point.Zero;
                animsToPlay.push(
                    this.startTween(
                        this.getScaleTween(
                            rewardElement,
                            rewardScale,
                            550,
                            Easing.Back.Out
                        )
                    )
                );
            }
            if (newOffset) {
                animsToPlay.push(
                    this.startTween(
                        this.getOffsetTween(rewardElement, newOffset, 1300)
                    )
                );
            }

            const rewardTextRenderable: Renderable = reward
                .findChildByName('amount_text')
                .getComponent(Renderable);
            rewardTextRenderable.alpha = 0;
            rewardTextRenderable.visible = true;
            animsToPlay.push(
                this.startTween(
                    this.getAlphaTween(rewardTextRenderable, 1, 1000)
                )
            );
        }

        return animsToPlay;
    }

    private getRewardOffsets(
        rewards: GameObject[],
        index: number,
        isLastDay: boolean
    ): tVector4 {
        return isLastDay
            ? lastDayRewardOffsets?.[rewards.length]?.[index]
            : defaultRewardOffsets?.[rewards.length]?.[index];
    }

    private getOffsetTween(
        elementComponent: ElementComponent,
        offsets: number[],
        duration: number = 250,
        easing: (k: number) => number = Easing.Exponential.InOut
    ): Tween {
        return this.getTween(elementComponent)
            .to(
                {
                    offsetX1: offsets[0],
                    offsetY1: offsets[1],
                    offsetX2: offsets[2],
                    offsetY2: offsets[3],
                },
                duration
            )
            .easing(easing);
    }

    private getScaleTween(
        elementComponent: ElementComponent,
        target: Point,
        duration: number = 250,
        easing: (k: number) => number = Easing.Exponential.InOut
    ): Tween {
        return this.getTween(elementComponent.scale)
            .to(target, duration)
            .easing(easing);
    }

    private getAlphaTween(
        renderable: Renderable,
        alpha: number,
        duration: number = 250,
        easing: (k: number) => number = Easing.Exponential.InOut
    ): Tween {
        return this.getTween(renderable)
            .to({ alpha }, duration)
            .easing(easing);
    }

    private *playCheckAnimation(
        gameObject: Armature,
        initialDelay: number
    ): IterableIterator<number> {
        const renderable: Renderable = gameObject.getComponent(Renderable);
        yield* delay(initialDelay);
        yield* AwaitIterators([
            PlayAnimation(
                new DragonbonesDefault(gameObject, {
                    playTimes: 1,
                })
            ),
            this.startTween(this.getAlphaTween(renderable, 1, 50)),
        ]);
    }
}
